import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import Grid from '@mui/material/Grid';
import ReservationCard from '../features/usersReservation/components/ReservationCard';
import { getUserReservation, createUserReservation, deleteUserReservation } from '../features/usersReservation/services/usersReservationService';
import { useAuth } from '../contexts/authContext';
import CircularProgress from '../components/CircularProgress';
import BackDropLoader from '../components/BackDropLoader';
import { Tab, Tabs, Typography } from '@mui/material';
import { isEqual, startOfDay, add } from 'date-fns';
import { getNewDate } from '../utils';

const tabFilter = Object.freeze({
  TODAY: 1,
  TOMORROW: 2,
  DAY_AFTER_TOMORROW: 3,
  RESERVED: 4,
  ALL: 5
});

export default function UsersReservationPage() {

  const auth = useAuth();
  const intl = useIntl();
  const [userReservationData, setUserReservationData] = useState({ isFetching: Boolean(auth.user.isActive), reservations: [] });
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [day, setDay] = useState(tabFilter.TODAY);

  const fetchData = useCallback(async (showSuccesMsg = true, showErrorMsg = true) => {
    try {
      const reservations = await getUserReservation(auth.user.id);
      if (showSuccesMsg) enqueueSnackbar(<FormattedMessage id='usersReservation.get.msg.success' defaultMessage='Users Reservation successfully fetched' />, {
        variant: 'success',
      });
      setUserReservationData({ isFetching: false, reservations });
    } catch (error) {
      if (showErrorMsg) enqueueSnackbar(<FormattedMessage id='usersReservation.get.msg.error' defaultMessage='Users Reservation fetch failed' />, {
        variant: 'error',
      });
      setUserReservationData({ isFetching: false,  reservations: [] });
    }
  }, [setUserReservationData]);

  useEffect(() => {
    if (auth.user.isActive) fetchData();
  }, []);

  const handleReserve = async (e) => {
    setLoading(true);
    try {
      const trainingID = e.target.value;
      await createUserReservation({ trainingID, userID: auth.user.id });
      await fetchData(false);
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='usersReservation.reserve.msg.error' defaultMessage='Reservation has failed' />, {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (e) => {
    setLoading(true);
    try {
      const trainingID = e.target.value;
      await deleteUserReservation(trainingID);
      await fetchData(false);
    } catch (error) {
      enqueueSnackbar(<FormattedMessage id='usersReservation.cancel.msg.error' defaultMessage='Cancel Reservation has failed' />, {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (event, newValue) => {
    setDay(newValue);
  };

  const filteredReservations = useMemo(() => filterReservationsByDate(userReservationData.reservations, day), [userReservationData.reservations, day]);

  return (
    userReservationData.isFetching ?
      <CircularProgress /> :
      <>
        <Tabs
          value={day}
          onChange={handleDayChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable auto tabs example"
          allowScrollButtonsMobile
          sx={{ marginBottom: 5 }}
        >
          <Tab value={tabFilter.TODAY} label={intl.formatMessage({ id: 'userReservation.tabs.today', defaultMessage: 'Today' })} />
          <Tab value={tabFilter.TOMORROW} label={intl.formatMessage({ id: 'userReservation.tabs.tomorrow', defaultMessage: 'Tomorrow' })} />
          <Tab value={tabFilter.DAY_AFTER_TOMORROW} label={intl.formatMessage({ id: 'userReservation.tabs.dayAfterTomorrow', defaultMessage: 'Day after tomorrow' })} />
          <Tab value={tabFilter.RESERVED} label={intl.formatMessage({ id: 'userReservation.tabs.reserved', defaultMessage: 'Reserved' })} />
          <Tab value={tabFilter.ALL} label={intl.formatMessage({ id: 'userReservation.tabs.all', defaultMessage: 'All' })} />
        </Tabs>
        <Grid container spacing={5} alignItems="flex-end">
          <BackDropLoader open={loading} />
          {
            filteredReservations.length ? filteredReservations.map(userReservation => (
              <Grid item key={userReservation.id} xs={12} sm={6} md={6} lg={4} xl={3}>
                <ReservationCard userReservation={userReservation} onReserve={handleReserve} onCancelReservation={handleCancelReservation} />
              </Grid>
            )) :
              <Grid item xs={12}>
                <Typography textAlign='center' variant='h5'>
                  <FormattedMessage id='common.noItems' defaultMessage='There are no items to display' />
                </Typography>
              </Grid>
          }
        </Grid>
      </>
  );
}

function filterReservationsByDate(reservations, day) {
  if (!reservations.length) return [];
  switch (day) {
    case tabFilter.TODAY:
      return reservations.filter(reservation => isEqual(startOfDay(getNewDate()), startOfDay(new Date(reservation.reservationTimeFrom))));
    case tabFilter.TOMORROW:
      return reservations.filter(reservation => isEqual(add(startOfDay(getNewDate()), { days: 1 }), startOfDay(new Date(reservation.reservationTimeFrom))));
    case tabFilter.DAY_AFTER_TOMORROW:
      return reservations.filter(reservation => isEqual(add(startOfDay(getNewDate()), { days: 2 }), startOfDay(new Date(reservation.reservationTimeFrom))));
    case tabFilter.RESERVED:
      return reservations.filter(reservation => reservation.userReserved);
    default:
      return reservations;
  }
}